import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { useTheme, styled, Grid, Typography, Button, Divider, Card, Select, FormControl } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';

import { STATUS_BAR_CLASS, STATUS_BAR_DISPLAY, gatherCatalog } from 'services/utils/enums';

import { useError } from 'contexts/ErrorContext';
import { useJobs, TYPES as JOB_TYPES } from 'services/job';
import { approveSupplementService, declineSupplementService, declineService, acceptService, supplementService, startService, completeService, updateService, subletService } from 'services/actions/service';
import { useCompany } from 'services/company';

import ServiceHistory from '../Service/History/ServiceHistoryModal';
import MediaWithPopup from 'components/MediaWithPopup';
import { mediaContentIsValid } from 'helpers/Media';
import { useChat } from '../../../../contexts/ChatContext';

const ApproveButton = styled(Button)(({ theme }) => ({
    textTransform: 'uppercase',
    borderRadius: theme.spacing(1),
    padding: '2px 8px',
    marginRight: theme.spacing(1.5),
    marginLeft: theme.spacing(1.5),
    background: theme.palette.blue[40],
    color: theme.palette.blue.main,
    '&: hover': {
        background: theme.palette.blue[40],
        color: theme.palette.blue.main,
    },
}));

const DeclineButton = styled(Button)(({ theme }) => ({
    textTransform: 'uppercase',
    borderRadius: theme.spacing(1),
    padding: '2px 8px',
    marginRight: theme.spacing(1.5),
    marginLeft: theme.spacing(1.5),
    background: theme.palette.grey[40],
    color: theme.palette.red.main,
    '&: hover': {
        background: theme.palette.grey[40],
        color: theme.palette.red.main,
    },
}));

const canChangeServiceSupplement = (service) => {
    return (service.service_status === 'Open' || service.service_status === 'Accepted' || service.service_status === 'In Progress') &&
        service?.supplement?.status === 'Pending Approval';
};

const ServiceDetails = ({ service, i, activeJob, rep }) => {
    const jobsContext = useJobs();
    const theme = useTheme();
    const err = useError();
    const chat = useChat();
    const companyContext = useCompany();
    const isRepairer = companyContext.state.company?.marketplace_side === 'Repairer';
    const [supplementPrice, setSupplementPrice] = useState('');
    const [showSupplementInput, setShowSupplementInput] = useState(false);
    const [catalog, setCatalog] = useState([]);
    const [supplementServiceType, setSupplementServiceType] = useState('');
    const [supplementRepairType, setSupplementRepairType] = useState('');
    const [supplementPosition, setSupplementPosition] = useState('');
    const [supplementPart, setSupplementPart] = useState('');
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [showSupplierEdit, setShowSupplierEdit] = useState(false);
    const [supplierEditPrice, setSupplierEditPrice] = useState('');
    const [supplierEditServiceType, setSupplierEditServiceType] = useState('');
    const [supplierEditRepairType, setSupplierEditRepairType] = useState('');
    const [supplierEditPosition, setSupplierEditPosition] = useState('');
    const [supplierEditPart, setSupplierEditPart] = useState('');
    const [showSubletForm, setShowSubletForm] = useState(false);
    const [subletCompanyId, setSubletCompanyId] = useState('');
    const [subletPrice, setSubletPrice] = useState('');

    const companySublets = companyContext.state.company?.sublets || [];
    const isSublet = !!companyContext.state.company?.parent_id;

    const assignedByCompany = useMemo(() => {
        if (!isSublet || !service?.service_history?.length) return null;
        const myName = companyContext.state.company?.name;
        if (!myName) return null;
        const parentEntry = service.service_history.find(
            entry => entry.repair_company && entry.repair_company !== myName
        );
        return parentEntry?.repair_company || null;
    }, [isSublet, service?.service_history, companyContext.state.company?.name]);

    const chatChannelUrl = useMemo(() => {
        const repairerCompanyId = service?.repair_company?.id;
        const channels = activeJob?.chat?.channels;
        if (!channels) return null;

        if (Array.isArray(channels)) {
            const found = channels.find(c => String(c.company_id) === String(repairerCompanyId));
            return found?.channel_url ?? channels[0]?.channel_url ?? null;
        }

        if (typeof channels === 'object') {
            const byId = channels?.[repairerCompanyId];
            if (byId?.channel_url) return byId.channel_url;
            const first = Object.values(channels)[0];
            return first?.channel_url ?? null;
        }

        return null;
    }, [activeJob?.chat?.channels, service?.repairer?.company_id]);

    const channelPayload = useMemo(() => {
        if (!chatChannelUrl) return null;
        return {
            url: chatChannelUrl,
            name: activeJob?.supplier_company?.name
                || service?.repairer?.company_name
                || 'Channel',
            data: JSON.stringify({
                jobId: activeJob?.id,
                vin: activeJob?.vin,
                serviceId: service?.id,
                companyId: service?.repairer?.company_id,
            }),
        };
    }, [
        chatChannelUrl,
        activeJob?.supplier_company?.name,
        activeJob?.id,
        activeJob?.vin,
        service?.id,
        service?.repairer?.company_id,
    ]);

    const handleOpenChat = React.useCallback(() => {
        if (!channelPayload) return;
        if (typeof chat?.openWithChannel === 'function') {
            chat.openWithChannel(channelPayload);
        } else {
            chat?.setChannel?.(channelPayload);
            chat?.restore?.();
        }
    }, [chat, channelPayload]);

    const handleHistoryView = async function (service, i) {
        let t = viewingHistory;
        t[i] = true;
        setViewing(service.id);
        await setViewingHistory(t);
    };

    const handleHistoryViewClose = async function() {
        resetViewing();
    };

    const handleSupplement = async function(decision, service, price) {
        try {
            jobsContext.DISPATCH({ type: JOB_TYPES.LOADING, payload: true });
            await (decision === 'approve' ?
                approveSupplementService({ serviceID: service }) :
                declineSupplementService({ serviceID: service, price: price })
            );
            await jobsContext.actions.refreshJob(activeJob.id);
        } catch (error) {
            console.log(error);
            // @ts-ignore
            err.DISPATCH({ type: 'API_ERROR', payload: error });
        } finally {
            jobsContext.DISPATCH({ type: JOB_TYPES.LOADING, payload: false });
        }
    };

    const handleServiceAction = async function(actionFn, serviceID) {
        try {
            jobsContext.DISPATCH({ type: JOB_TYPES.LOADING, payload: true });
            await actionFn({ serviceID });
            await jobsContext.actions.refreshJob(activeJob.id);
        } catch (error) {
            console.log(error);
            err.DISPATCH({ type: 'API_ERROR', payload: error });
        } finally {
            jobsContext.DISPATCH({ type: JOB_TYPES.LOADING, payload: false });
        }
    };

    // Shared catalog cascade resolution
    const resolveCatalogMatch = (sType, rType, pos, part) => {
        let matches = catalog.filter(item => item.service_type === sType);
        const hasRepairTypes = matches.some(item => !!item.repair_type);
        if (hasRepairTypes) {
            if (rType === '__none__') matches = matches.filter(item => !item.repair_type);
            else if (rType) matches = matches.filter(item => item.repair_type === rType);
        } else {
            matches = matches.filter(item => !item.repair_type);
        }
        const hasPositions = matches.some(item => !!item.position);
        if (hasPositions) {
            if (pos === '__none__') matches = matches.filter(item => !item.position);
            else if (pos) matches = matches.filter(item => item.position === pos);
        } else {
            matches = matches.filter(item => !item.position);
        }
        const hasParts = matches.some(item => !!item.part);
        if (hasParts) {
            if (part === '__none__') matches = matches.filter(item => !item.part);
            else if (part) matches = matches.filter(item => item.part === part);
        } else {
            matches = matches.filter(item => !item.part);
        }
        return matches.length >= 1 ? matches[0].id : null;
    };

    // Compute cascade state for supplement dropdowns
    const suppBySType = supplementServiceType ? catalog.filter(item => item.service_type === supplementServiceType) : [];
    const suppRepairTypes = [...new Set(suppBySType.map(item => item.repair_type).filter(Boolean))];
    const suppHasNullRT = suppBySType.some(item => !item.repair_type);
    const suppRTResolved = suppRepairTypes.length > 0 ? supplementRepairType : '__none__';
    const suppRTReady = suppRepairTypes.length === 0 || supplementRepairType !== '';
    const suppByRType = suppRTReady ? suppBySType.filter(item =>
        suppRTResolved === '__none__' ? !item.repair_type : item.repair_type === suppRTResolved
    ) : [];
    const suppPositions = [...new Set(suppByRType.map(item => item.position).filter(Boolean))];
    const suppHasNullPos = suppByRType.some(item => !item.position);
    const suppPosResolved = suppPositions.length > 0 ? supplementPosition : '__none__';
    const suppPosReady = suppPositions.length === 0 || supplementPosition !== '';
    const suppByPos = suppPosReady ? suppByRType.filter(item =>
        suppPosResolved === '__none__' ? !item.position : item.position === suppPosResolved
    ) : [];
    const suppParts = [...new Set(suppByPos.map(item => item.part).filter(Boolean))];
    const suppHasNullPart = suppByPos.some(item => !item.part);
    const suppPartResolved = suppParts.length > 0 ? supplementPart : '__none__';

    // Compute cascade state for supplier edit dropdowns
    const editBySType = supplierEditServiceType ? catalog.filter(item => item.service_type === supplierEditServiceType) : [];
    const editRepairTypes = [...new Set(editBySType.map(item => item.repair_type).filter(Boolean))];
    const editHasNullRT = editBySType.some(item => !item.repair_type);
    const editRTResolved = editRepairTypes.length > 0 ? supplierEditRepairType : '__none__';
    const editRTReady = editRepairTypes.length === 0 || supplierEditRepairType !== '';
    const editByRType = editRTReady ? editBySType.filter(item =>
        editRTResolved === '__none__' ? !item.repair_type : item.repair_type === editRTResolved
    ) : [];
    const editPositions = [...new Set(editByRType.map(item => item.position).filter(Boolean))];
    const editHasNullPos = editByRType.some(item => !item.position);
    const editPosResolved = editPositions.length > 0 ? supplierEditPosition : '__none__';
    const editPosReady = editPositions.length === 0 || supplierEditPosition !== '';
    const editByPos = editPosReady ? editByRType.filter(item =>
        editPosResolved === '__none__' ? !item.position : item.position === editPosResolved
    ) : [];
    const editParts = [...new Set(editByPos.map(item => item.part).filter(Boolean))];
    const editHasNullPart = editByPos.some(item => !item.part);
    const editPartResolved = editParts.length > 0 ? supplierEditPart : '__none__';

    const handleSubmitSupplement = async function(serviceID, price) {
        try {
            jobsContext.DISPATCH({ type: JOB_TYPES.LOADING, payload: true });
            const data = {};
            if (price !== '' && price != null) data.price = parseFloat(price);
            const serviceDetailId = resolveCatalogMatch(supplementServiceType, supplementRepairType, supplementPosition, supplementPart);
            if (serviceDetailId) data['service_detail_id'] = serviceDetailId;
            await supplementService({ serviceID, data });
            await jobsContext.actions.refreshJob(activeJob.id);
            setShowSupplementInput(false);
            setSupplementPrice('');
            setSupplementServiceType('');
            setSupplementRepairType('');
            setSupplementPosition('');
            setSupplementPart('');
        } catch (error) {
            console.log(error);
            err.DISPATCH({ type: 'API_ERROR', payload: error });
        } finally {
            jobsContext.DISPATCH({ type: JOB_TYPES.LOADING, payload: false });
        }
    };

    const handleOpenSupplementInput = async function() {
        setShowSupplementInput(true);
        if (!catalog.length) {
            setCatalogLoading(true);
            try {
                const res = await gatherCatalog({});
                if (res?.data?.payload?.length) {
                    setCatalog(res.data.payload);
                }
            } catch (error) {
                console.error('Failed to load catalog:', error);
            } finally {
                setCatalogLoading(false);
            }
        }
    };

    const handleSubmitSublet = async function(serviceID) {
        try {
            jobsContext.DISPATCH({ type: JOB_TYPES.LOADING, payload: true });
            // eslint-disable-next-line camelcase
            const data = { sublet_company_id: subletCompanyId };
            if (subletPrice !== '' && subletPrice != null) {
                data.price = parseFloat(subletPrice);
            }
            await subletService({ serviceID, data });
            await jobsContext.actions.refreshJob(activeJob.id);
            setShowSubletForm(false);
            setSubletCompanyId('');
            setSubletPrice('');
        } catch (error) {
            console.log(error);
            err.DISPATCH({ type: 'API_ERROR', payload: error });
        } finally {
            jobsContext.DISPATCH({ type: JOB_TYPES.LOADING, payload: false });
        }
    };

    const handleOpenSupplierEdit = async function() {
        setShowSupplierEdit(true);
        setSupplierEditPrice(service.price || '');
        setSupplierEditServiceType('');
        setSupplierEditRepairType('');
        setSupplierEditPosition('');
        setSupplierEditPart('');
        if (!catalog.length) {
            setCatalogLoading(true);
            try {
                const res = await gatherCatalog({});
                if (res && res.data.payload) {
                    setCatalog(res.data.payload);
                }
            } catch (error) {
                console.error('Failed to load catalog:', error);
            } finally {
                setCatalogLoading(false);
            }
        }
    };

    const handleSubmitSupplierEdit = async function(serviceID) {
        try {
            jobsContext.DISPATCH({ type: JOB_TYPES.LOADING, payload: true });
            const data = {};
            const newPrice = parseFloat(supplierEditPrice);
            if (!isNaN(newPrice) && newPrice !== service.price) {
                data.price = newPrice;
            }
            {
                const serviceDetailId = resolveCatalogMatch(supplierEditServiceType, supplierEditRepairType, supplierEditPosition, supplierEditPart);
                if (serviceDetailId) data['service_detail_id'] = serviceDetailId;
            }
            if (Object.keys(data).length === 0) {
                setShowSupplierEdit(false);
                return;
            }
            await updateService({ serviceID, data });
            await jobsContext.actions.refreshJob(activeJob.id);
            setShowSupplierEdit(false);
            setSupplierEditPrice('');
            setSupplierEditServiceType('');
            setSupplierEditRepairType('');
            setSupplierEditPosition('');
            setSupplierEditPart('');
        } catch (error) {
            console.log(error);
            err.DISPATCH({ type: 'API_ERROR', payload: error });
        } finally {
            jobsContext.DISPATCH({ type: JOB_TYPES.LOADING, payload: false });
        }
    };

    const [viewingHistory, setViewingHistory] = useState([]);
    const [viewing, setViewing] = useState(0);
    let renderedMediaCount = 0;

    const resetViewing = async function () {
        const v = activeJob.services.map(() => false);
        await setViewingHistory(v);
        await setViewing(0);
    };

    const formatServiceDetailsTitle = () => {
        let out = '';
        let position;
        let part;

        if (service.service_details.position) {
            position = service.service_details.position.replace('N/A', '');
        }

        if (service.service_details.part) {
            part = service.service_details.part.replace('N/A', '');
        }

        let positionPart;

        if (position && part) {
            positionPart = `${position} ${part}`;
        } else if (position) {
            positionPart = position;
        } else if (part) {
            positionPart = part;
        }

        if (service.service_details.repair_type && positionPart) {
            out = `${service.service_details.repair_type}, ${positionPart}`;
        } else if (service.service_details.repair_type) {
            out = service.service_details.repair_type;
        } else if (positionPart) {
            out = positionPart;
        }

        return out;
    };

    const isInvoiceMedia = (mediaSubmissionId) => {
        let isInvoice = false;

        for (const elem of service?.service_history || {}) {
            if (elem.media_submission_ids && elem.media_submission_ids.includes(mediaSubmissionId) && elem.invoice) {
                isInvoice = true;
                break;
            }
        }

        return isInvoice;
    };

    useEffect(() => {
        if (activeJob.services && activeJob.services.length && !viewingHistory.length) resetViewing();
    }, [viewing, viewingHistory]);

    const supplementControls = canChangeServiceSupplement(service) && !isRepairer ? (
        <Card variant={'outlined'} elevation={0} sx={{ width:'100%', p: 1, borderRadius: 2 }}>
            <Grid display={'flex'} justifyContent={'center'}>
                {/* @ts-ignore */}
                <Card variant={'SUPPLEMENT_PENDING'}> Supplement Pending </Card>
            </Grid>
            <Grid display={'flex'} justifyContent={'center'} mb={1}>
                <Typography variant={'h6'}> { `Service has pending supplemental work from ${rep}` } </Typography>
            </Grid>
            {service?.supplement?.pending_service_type && (
                <Grid display={'flex'} justifyContent={'center'} mb={1}>
                    <Typography sx={{ fontSize: 13, fontWeight: 400 }} color={theme.palette.grey[700]}>
                        Requested service type change: <strong>{service.supplement.pending_service_type}</strong>
                    </Typography>
                </Grid>
            )}
            <Grid display={'flex'} justifyContent={'center'}>
                <ApproveButton variant='contained' onClick={() => {handleSupplement('approve', service.id);}}>
                    Approve
                </ApproveButton>
                <DeclineButton
                    variant='contained'
                    onClick={() => {
                        handleSupplement(
                            'decline',
                            service.id,
                            service.service_history ?
                                // @ts-ignore
                                service.service_history.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0].price :
                                service.price,
                        );
                    }}>
                        Decline
                </DeclineButton>
            </Grid>
        </Card>
    ) : null;

    const canRepairerAct = isRepairer && !service.rework && !(service.onHold ?? service.on_hold) && !service.supplementIsPending && service?.supplement?.status !== 'Pending Approval';
    const status = service.service_status;

    const repairerControls = isRepairer ? (
        <Card variant={'outlined'} elevation={0} sx={{ width:'100%', p: 1, borderRadius: 2, mt: 1 }}>
            <Grid display={'flex'} justifyContent={'center'} gap={1} flexWrap={'wrap'}>
                {canRepairerAct && status === 'Open' && (
                    <>
                        <ApproveButton variant='contained' onClick={() => handleServiceAction(acceptService, service.id)}>
                            Accept
                        </ApproveButton>
                        <DeclineButton variant='contained' onClick={() => handleServiceAction(declineService, service.id)}>
                            Decline
                        </DeclineButton>
                    </>
                )}
                {canRepairerAct && status === 'Accepted' && (
                    <>
                        <ApproveButton variant='contained' onClick={() => handleServiceAction(startService, service.id)}>
                            Start
                        </ApproveButton>
                        <DeclineButton variant='contained' onClick={() => handleServiceAction(declineService, service.id)}>
                            Unaccept
                        </DeclineButton>
                    </>
                )}
                {canRepairerAct && status === 'In Progress' && (
                    <>
                        <ApproveButton variant='contained' onClick={() => handleServiceAction(completeService, service.id)}>
                            Complete
                        </ApproveButton>
                        <DeclineButton variant='contained' onClick={() => handleServiceAction(declineService, service.id)}>
                            Decline
                        </DeclineButton>
                    </>
                )}
                {canRepairerAct && ['Open', 'Accepted', 'In Progress'].includes(status) && !showSupplementInput && (
                    <Button
                        variant='outlined'
                        sx={{ textTransform: 'none', borderRadius: 1, fontSize: 12 }}
                        onClick={handleOpenSupplementInput}
                    >
                        Request Supplement
                    </Button>
                )}
                {canRepairerAct && ['Open', 'Accepted', 'In Progress'].includes(status) && companySublets.length > 0 && !showSubletForm && (
                    <Button
                        variant='outlined'
                        sx={{ textTransform: 'none', borderRadius: 1, fontSize: 12 }}
                        onClick={() => setShowSubletForm(true)}
                    >
                        Sublet
                    </Button>
                )}
                {showSubletForm && (
                    <Grid display={'flex'} flexDirection={'column'} gap={1} sx={{ width: '100%', mt: 1 }}>
                        <FormControl size='small' fullWidth>
                            <Select
                                native
                                value={subletCompanyId}
                                onChange={(e) => setSubletCompanyId(e.target.value)}
                                sx={{ fontSize: 13 }}
                            >
                                <option value='' disabled>Select Sublet Company</option>
                                {companySublets.map(sub => (
                                    <option key={sub.id} value={sub.id}>{sub.name}</option>
                                ))}
                            </Select>
                        </FormControl>
                        <Grid display={'flex'} alignItems={'center'} gap={1}>
                            <Typography sx={{ fontSize: 13 }}>Price: $</Typography>
                            <input
                                type='number'
                                step='0.01'
                                value={subletPrice}
                                onChange={(e) => setSubletPrice(e.target.value)}
                                style={{ width: 100, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' }}
                            />
                            <ApproveButton
                                variant='contained'
                                disabled={!subletCompanyId}
                                onClick={() => handleSubmitSublet(service.id)}
                            >
                                Submit
                            </ApproveButton>
                            <DeclineButton variant='contained' onClick={() => { setShowSubletForm(false); setSubletCompanyId(''); setSubletPrice(''); }}>
                                Cancel
                            </DeclineButton>
                        </Grid>
                    </Grid>
                )}
                {showSupplementInput && (
                    <Grid display={'flex'} flexDirection={'column'} gap={1} sx={{ width: '100%', mt: 1 }}>
                        <FormControl size='small' fullWidth>
                            <Select
                                native
                                value={supplementServiceType}
                                onChange={(e) => { setSupplementServiceType(e.target.value); setSupplementRepairType(''); setSupplementPosition(''); setSupplementPart(''); }}
                                disabled={catalogLoading}
                                sx={{ fontSize: 13 }}
                            >
                                <option value='' disabled>Select Service Type</option>
                                {[...new Set(catalog.map(item => item.service_type))].filter(Boolean).map(st => (
                                    <option key={st} value={st}>{st}</option>
                                ))}
                            </Select>
                        </FormControl>
                        {supplementServiceType && suppRepairTypes.length > 0 && (
                            <FormControl size='small' fullWidth>
                                <Select
                                    native
                                    value={supplementRepairType}
                                    onChange={(e) => { setSupplementRepairType(e.target.value); setSupplementPosition(''); setSupplementPart(''); }}
                                    disabled={catalogLoading}
                                    sx={{ fontSize: 13 }}
                                >
                                    <option value='' disabled>Select Repair Type</option>
                                    {suppHasNullRT && <option value='__none__'>None</option>}
                                    {suppRepairTypes.map(rt => (
                                        <option key={rt} value={rt}>{rt}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        {supplementServiceType && suppRTReady && suppPositions.length > 0 && (
                            <FormControl size='small' fullWidth>
                                <Select
                                    native
                                    value={supplementPosition}
                                    onChange={(e) => { setSupplementPosition(e.target.value); setSupplementPart(''); }}
                                    disabled={catalogLoading}
                                    sx={{ fontSize: 13 }}
                                >
                                    <option value='' disabled>Select Position</option>
                                    {suppHasNullPos && <option value='__none__'>None</option>}
                                    {suppPositions.map(pos => (
                                        <option key={pos} value={pos}>{pos}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        {supplementServiceType && suppRTReady && suppPosReady && suppParts.length > 0 && (
                            <FormControl size='small' fullWidth>
                                <Select
                                    native
                                    value={supplementPart}
                                    onChange={(e) => setSupplementPart(e.target.value)}
                                    disabled={catalogLoading}
                                    sx={{ fontSize: 13 }}
                                >
                                    <option value='' disabled>Select Part</option>
                                    {suppHasNullPart && <option value='__none__'>None</option>}
                                    {suppParts.map(prt => (
                                        <option key={prt} value={prt}>{prt}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <Grid display={'flex'} alignItems={'center'} gap={1}>
                            <Typography sx={{ fontSize: 13 }}>New Price: $</Typography>
                            <input
                                type='number'
                                step='0.01'
                                value={supplementPrice}
                                onChange={(e) => setSupplementPrice(e.target.value)}
                                style={{ width: 100, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' }}
                            />
                            <ApproveButton variant='contained' onClick={() => handleSubmitSupplement(service.id, supplementPrice)}>
                                Submit
                            </ApproveButton>
                            <DeclineButton variant='contained' onClick={() => { setShowSupplementInput(false); setSupplementPrice(''); setSupplementServiceType(''); setSupplementRepairType(''); setSupplementPosition(''); setSupplementPart(''); }}>
                                Cancel
                            </DeclineButton>
                        </Grid>
                    </Grid>
                )}
                {canChangeServiceSupplement(service) && isRepairer && (
                    <Grid display={'flex'} justifyContent={'center'} sx={{ width: '100%' }}>
                        <Card variant={'SUPPLEMENT_PENDING'}> Supplement Pending Approval </Card>
                    </Grid>
                )}
            </Grid>
        </Card>
    ) : null;

    const supplierEditControls = !isRepairer && status === 'Open' && !canChangeServiceSupplement(service) ? (
        <Card variant={'outlined'} elevation={0} sx={{ width:'100%', p: 1, borderRadius: 2, mt: 1 }}>
            <Grid display={'flex'} justifyContent={'center'} gap={1} flexWrap={'wrap'}>
                {!showSupplierEdit && (
                    <Button
                        variant='outlined'
                        sx={{ textTransform: 'none', borderRadius: 1, fontSize: 12 }}
                        onClick={handleOpenSupplierEdit}
                    >
                        Edit Service
                    </Button>
                )}
                {showSupplierEdit && (
                    <Grid display={'flex'} flexDirection={'column'} gap={1} sx={{ width: '100%' }}>
                        <FormControl size='small' fullWidth>
                            <Select
                                native
                                value={supplierEditServiceType}
                                onChange={(e) => { setSupplierEditServiceType(e.target.value); setSupplierEditRepairType(''); setSupplierEditPosition(''); setSupplierEditPart(''); }}
                                disabled={catalogLoading}
                                sx={{ fontSize: 13 }}
                            >
                                <option value='' disabled>Select Service Type</option>
                                {[...new Set(catalog.map(item => item.service_type))].filter(Boolean).map(st => (
                                    <option key={st} value={st}>{st}</option>
                                ))}
                            </Select>
                        </FormControl>
                        {supplierEditServiceType && editRepairTypes.length > 0 && (
                            <FormControl size='small' fullWidth>
                                <Select
                                    native
                                    value={supplierEditRepairType}
                                    onChange={(e) => { setSupplierEditRepairType(e.target.value); setSupplierEditPosition(''); setSupplierEditPart(''); }}
                                    disabled={catalogLoading}
                                    sx={{ fontSize: 13 }}
                                >
                                    <option value='' disabled>Select Repair Type</option>
                                    {editHasNullRT && <option value='__none__'>None</option>}
                                    {editRepairTypes.map(rt => (
                                        <option key={rt} value={rt}>{rt}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        {supplierEditServiceType && editRTReady && editPositions.length > 0 && (
                            <FormControl size='small' fullWidth>
                                <Select
                                    native
                                    value={supplierEditPosition}
                                    onChange={(e) => { setSupplierEditPosition(e.target.value); setSupplierEditPart(''); }}
                                    disabled={catalogLoading}
                                    sx={{ fontSize: 13 }}
                                >
                                    <option value='' disabled>Select Position</option>
                                    {editHasNullPos && <option value='__none__'>None</option>}
                                    {editPositions.map(pos => (
                                        <option key={pos} value={pos}>{pos}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        {supplierEditServiceType && editRTReady && editPosReady && editParts.length > 0 && (
                            <FormControl size='small' fullWidth>
                                <Select
                                    native
                                    value={supplierEditPart}
                                    onChange={(e) => setSupplierEditPart(e.target.value)}
                                    disabled={catalogLoading}
                                    sx={{ fontSize: 13 }}
                                >
                                    <option value='' disabled>Select Part</option>
                                    {editHasNullPart && <option value='__none__'>None</option>}
                                    {editParts.map(prt => (
                                        <option key={prt} value={prt}>{prt}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <Grid display={'flex'} alignItems={'center'} gap={1}>
                            <Typography sx={{ fontSize: 13 }}>Price: $</Typography>
                            <input
                                type='number'
                                step='0.01'
                                value={supplierEditPrice}
                                onChange={(e) => setSupplierEditPrice(e.target.value)}
                                style={{ width: 100, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc' }}
                            />
                            <ApproveButton variant='contained' onClick={() => handleSubmitSupplierEdit(service.id)}>
                                Save
                            </ApproveButton>
                            <DeclineButton variant='contained' onClick={() => { setShowSupplierEdit(false); setSupplierEditPrice(''); setSupplierEditServiceType(''); setSupplierEditRepairType(''); setSupplierEditPosition(''); setSupplierEditPart(''); }}>
                                Cancel
                            </DeclineButton>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </Card>
    ) : null;

    const makeChatButton = () => {
        if (!channelPayload) return null;

        return (
            <Button fullWidth title={`btn-chat-${i}`} onClick={handleOpenChat}>
                <Grid sx={{ width: '100%' }} display='flex' justifyContent='space-between' alignItems='center' p={0} m={0} my={0.5}>
                    <Typography sx={{ fontSize: 12, fontWeight: 500 }} color={theme.palette.blue[500]}>
                        {isRepairer ? 'Chat with supplier' : 'Chat with repairer'}
                    </Typography>
                    <ChevronRight />
                </Grid>
            </Button>
        );
    };

    return (
        <div key={i}>
            <Card variant='outlined' sx={{ borderRadius: 2, my: 2, p: 2 }}>
                <Grid display={'flex'} justifyContent={'space-between'}p={0} m={0} mb={1.5} alignItems={'center'}>
                    <Grid display={'flex'} alignItems={'center'}>
                        <Typography sx={{ fontSize: 14, fontWeight: 500 }} color={theme.palette.grey[900]}>
                            { service.service_details.service_type || '' }
                        </Typography>
                        &nbsp;
                        <Typography sx={{ fontSize: 12, fontWeight: 400 }} color={theme.palette.grey[500]}>
                            { formatServiceDetailsTitle() }
                        </Typography>
                    </Grid>
                    <Grid display={'flex'} gap={1}>
                        { !service?.service_history?.length ?
                            null :
                            service.service_history
                                .map(el => el.price)
                                .filter(el => el && el != service.price)
                                .map((el, ind) => (
                                    <Typography
                                        key={ind}
                                        color={theme.palette.grey[500]}
                                        sx={{ fontSize: 12, textDecoration: 'line-through', fontWeight: 300 }}
                                    >
                                        { new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(el) }
                                    </Typography>
                                ))
                        }
                        {(service?.supplement?.status === 'Pending Approval' && service?.supplement?.price !== null) ?
                            <React.Fragment>
                                <Typography
                                    color={theme.palette.grey[500]}
                                    sx={{ fontSize: 12, textDecoration: 'line-through', fontWeight: 300 }}
                                >
                                    { new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(service.price) }
                                </Typography>
                                <Typography sx={{ fontSize: 12, fontWeight: 500 }} color={theme.palette.grey[900]} display={'flex'}>
                                    { new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(service.supplement.price) }
                                </Typography>
                            </React.Fragment>
                            :
                            <Typography sx={{ fontSize: 12, fontWeight: 500 }} color={theme.palette.grey[900]} display={'flex'}>
                                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(service.price)}
                            </Typography>
                        }
                    </Grid>
                </Grid>
                {isRepairer && service.repair_company?.name && companyContext.state.company?.name && service.repair_company.name !== companyContext.state.company.name && (
                    <Typography sx={{ fontSize: 12, fontWeight: 500, mb: 0.5 }} color={theme.palette.blue[700]}>
                        Subletted to {service.repair_company.name}
                    </Typography>
                )}
                {isRepairer && isSublet && assignedByCompany && (
                    <Typography sx={{ fontSize: 12, fontWeight: 500, mb: 0.5 }} color={theme.palette.blue[700]}>
                        Assigned by {assignedByCompany}
                    </Typography>
                )}
                <Grid
                    display={'flex'}
                    justifyContent={'start'}
                    alignItems={'center'} p={0} m={0} my={0.5}
                >
                    <Card
                        variant={STATUS_BAR_CLASS[service.service_status]}
                    >
                        { STATUS_BAR_DISPLAY[service.service_status] }
                    </Card>
                    { (canChangeServiceSupplement(service)) && (
                        <Card
                            variant={STATUS_BAR_CLASS[service.supplement.status]}
                        >
                            { `Supplement ${service.supplement.status}` }
                        </Card>
                    )}
                    { service?.supplement?.type === 'Rework' && (
                        // @ts-ignore
                        <Card variant={'REWORK'}>Rework</Card>
                    )}
                </Grid>
                <Divider />
                {
                    !service?.media?.length ? null : (
                        <Grid
                            justifyContent={'start'}
                            alignItems={'center'} p={0} m={0} my={1.5} gap={1}
                        >
                            { service.media.map((serv) => {
                                return serv.submission_content
                                    .filter(m => m.media_type == 'Text').map((el, i) => {
                                        ++renderedMediaCount;
                                        return (
                                            <Typography
                                                key={i}
                                                sx={{ fontSize: 14, fontWeight: 400 }}
                                                color={theme.palette.grey[900]}
                                            >
                                                { el.text }
                                            </Typography>
                                        );
                                    });
                            })}
                        </Grid>
                    )
                }
                {
                    !service?.media?.length ? null : (
                        <Grid display={'flex'} justifyContent={'start'} alignItems={'center'} p={0} m={0} my={1.5} gap={1}>
                            { service.media.map((serv) => {
                                return serv.submission_content
                                    .filter(m => m.media_type != 'Text').map((media, i) => {
                                        if (mediaContentIsValid(media)) {
                                            ++renderedMediaCount;
                                            return (
                                                <MediaWithPopup
                                                    key={i}
                                                    url={media.url}
                                                    title={media.text || ''}
                                                    type={media.media_type}
                                                    width='65px'
                                                    height='65px'
                                                    isInvoice={isInvoiceMedia(serv.submission_id)}
                                                />
                                            );
                                        }
                                    });
                            })}
                        </Grid>
                    )
                }
                {
                    (renderedMediaCount > 0)
                        ? <Divider />
                        : null
                }
                <Button fullWidth={true} title={`btn-edt-${i}`} onClick={() => handleHistoryView(service, i)} disabled={viewingHistory[i]}>
                    <Grid sx={{ width:'100%' }} display={'flex'} justifyContent={'space-between'} alignItems={'center'} p={0} m={0} my={.5}>
                        <Typography sx={{ fontSize: 12, fontWeight: 500 }} color={theme.palette.blue[500]}> History </Typography>
                        <ChevronRight />
                    </Grid>
                </Button>
                {
                    viewing != service.id ? null : (
                        <ServiceHistory
                            open={viewing == service.id}
                            onClose={() => handleHistoryViewClose()}
                            service={service}
                            supplier={activeJob?.supplier_company?.name || ''}
                            viewerCompanyName={companyContext.state.company?.name || ''}
                        />
                    )
                }
                <Divider />
                { makeChatButton() }
                {supplementControls}
                {repairerControls}
                {supplierEditControls}
            </Card>
        </div>
    );
};

ServiceDetails.propTypes = {
    service: PropTypes.object,
    i: PropTypes.number,
    activeJob: PropTypes.object,
    rep: PropTypes.string,
};

export default ServiceDetails;
